import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { AccordionModule } from 'primeng/accordion';
import { CheckboxModule } from 'primeng/checkbox';
import { DynamicForm } from '../dynamic-form/dynamic-form';
import { ItemDynamicForm } from '../item-dynamic-form/item-dynamic-form';
import { MaintenanceRequestService } from '../services/asset-maintenance-request/asset-maintenance-request.service';
import { AssetService } from '../services/asset/asset.service';
import { MastersService } from '../services/masters/masters.service';
import { SubsidiaryService } from '../services/subsidiary/subsidiary.service';
import { ToasterService } from '@ui-nuvertos/auth';
import { lastValueFrom, Subject } from 'rxjs';
import { PageActionService } from '../services/page-action/page-action.service';
import { PayloadGeneratorService, PayloadValidationError } from '../services/payload/payload-generator.service';
import {
    MaintenanceRequestCreatePayload,
    MAINTENANCE_REQUEST_FIELD_TO_INTERFACE_MAP,
    MAINTENANCE_REQUEST_SKIP_FIELDS
} from './backend-payload.interface';
import { AssetHistoryModalComponent } from '../asset-create/asset-history-modal.component';
import { S3FileUploadService } from '../services/s3/s3-file-upload.service';
import { UserService } from '../services/user/user.service';
import { VendorService } from '../services/vendor/vendor.service';
import { InvoiceService } from '../services/vendor/invoice.service';
import { STANDALONE_MODE } from '../utils/standalone.utils';

@Component({
    selector: 'lib-maintenance-request',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ButtonModule,
        TabsModule,
        AccordionModule,
        CheckboxModule,
        DynamicForm,
        ItemDynamicForm,
        AssetHistoryModalComponent
    ],
    templateUrl: './asset-maintenance-request.html',
    styleUrl: './asset-maintenance-request.css'
})
export class MaintenanceRequest implements OnInit, OnDestroy {
    form: FormGroup;
    private destroy$ = new Subject<void>();

    isLoading = false;
    isSubmitting = false;
    historyModalVisible = false;
    formFieldFileNamesMap: Map<string, string> = new Map();

    allFields: any[] = [];
    primaryFields: any[] = [];
    dynamicFormTabs: any[] = [];

    templates: any[] = [];
    currentTemplateId: string | null = null;
    assetId: string | null = null;
    selectedAssetId: string | null = null;
    
    // Mode properties
    mode: 'create' | 'view' | 'edit' = 'create';
    maintenanceRequestId: string | null = null;
    maintenanceRequestData: any = null;

    // Stored data
    assetData: any = null;

    // Master data
    subsidiaryOptions: any[] = [];
    locationOptions: any[] = [];
    subLocationOptions: any[] = [];
    allSubLocations: any[] = [];
    subsidiaries: any[] = [];
    departmentOptions: any[] = [];
    assetGroupOptions: any[] = [];
    assetSubGroupOptions: any[] = [];
    maintenanceTypeOptions: any[] = [];
    maintenanceIssueTypeOptions: any[] = [];
    maintenanceWarrantyTypeOptions: any[] = [];

    // Maintenance schedule table data (from asset)
    maintenanceScheduleData: any[] = [];

    // Options map for item-dynamic-form dropdowns
    optionsMap = new Map<string, any[]>();

    // Feature flag: when true, converts autocomplete fields to plain input (standalone mode without accounting module)
    private readonly STANDALONE_MODE = STANDALONE_MODE;
    private readonly STANDALONE_CONVERT_TO_INPUT = ['pi'];

    // Fulfillment tab: field visibility based on assign_request_to
    private readonly FULFILLMENT_IN_HOUSE_FIELDS = [
        'assign_to_user_name', 'department', 'expected_start_date', 'expected_resolution_date'
    ];
    private readonly FULFILLMENT_VENDOR_FIELDS = [
        'vendor_name', 'contact_person', 'contact_number', 'covered_under',
        'estimated_repair_cost', 'need_to_send_out', 'send_to_location',
        'dispatch_date', 'expected_return_date', 'transport_mode', 'pickup_challan'
    ];

    // Fields that become non-editable once data is set from backend
    private readonly LOCK_FIELDS_ON_DATA = [
        'form_template_id', 'request_date', 'asset_name',
        'assign_request_to', 'vendor_name', 'assign_to_user_name',
        'parts_replaced', 'received_by_user_name', 'pi',
        'is_asset_operational', 'is_vendor_likely_required', 'request_to_discard'
    ];

    // Controls part_items table visibility
    showPartItemsTable = false;

    // Controls whether Work Details tab (tab 5) is shown — only if fulfillment data exists
    showWorkDetailsTab = false;
    // Force view mode on fulfillment/work-detail fields when data exists from backend
    fulfillmentDataExists = false;
    workDetailDataExists = false;

    // Work Details tab: field visibility based on assign_request_to
    private readonly WORK_DETAIL_IN_HOUSE_FIELDS = [
        'initial_diagnosis', 'is_asset_operational', 'is_vendor_likely_required', 'request_to_discard'
    ];
    private readonly WORK_DETAIL_VENDOR_FIELDS = [
        'return_fixed_date', 'received_by_user_name', 'return_mode', 'delivery_challan',
        'maintenance_status', 'vendor_notes', 'pi', 'claim_no', 'amount',
        'asset_condition', 'functional_test_done'
    ];

    constructor(
        private fb: FormBuilder,
        private maintenanceRequestService: MaintenanceRequestService,
        private assetService: AssetService,
        private mastersService: MastersService,
        private subsidiaryService: SubsidiaryService,
        private router: Router,
        private route: ActivatedRoute,
        private toasterService: ToasterService,
        private cdr: ChangeDetectorRef,
        private pageActionService: PageActionService,
        private payloadGenerator: PayloadGeneratorService,
        private s3FileUploadService: S3FileUploadService,
        private userService: UserService,
        private vendorService: VendorService,
        private invoiceService: InvoiceService
    ) {
        this.form = this.fb.group({});
    }

    async ngOnInit() {
        this.pageActionService.setActions([]);
        this.isLoading = true;

        // Read mode from query params first, then route data
        const queryMode = this.route.snapshot.queryParamMap.get('mode') as 'create' | 'edit' | 'view' | null;
        this.mode = queryMode || this.route.snapshot.data['mode'] || 'create';

        if (this.mode === 'view' || this.mode === 'edit') {
            this.maintenanceRequestId = this.route.snapshot.paramMap.get('id');
        } else {
            this.assetId = this.route.snapshot.queryParamMap.get('asset_id') || this.route.snapshot.paramMap.get('id');
        }

        await Promise.all([
            this.fetchMasters(),
            this.loadSubsidiaries()
        ]);

        try {
            // In View/Edit mode, fetch the saved Request first so we know which asset and template to use
            if ((this.mode === 'view' || this.mode === 'edit') && this.maintenanceRequestId) {
                const mrResp = await lastValueFrom(this.maintenanceRequestService.getMaintenanceRequest(this.maintenanceRequestId));
                this.maintenanceRequestData = mrResp?.data || mrResp || {};
                this.assetId = String(this.maintenanceRequestData.asset_id);
                this.currentTemplateId = this.maintenanceRequestData.form_template_id ? String(this.maintenanceRequestData.form_template_id) : null;
            }

            // Detect if fulfillment/work-detail data exists from backend (before processFields)
            if (this.maintenanceRequestData) {
                const mr = this.maintenanceRequestData;
                this.fulfillmentDataExists = !!(mr.fulfillment && mr.fulfillment.assign_request_to);
                this.workDetailDataExists = !!(mr.work_detail && (mr.work_detail.initial_diagnosis || mr.work_detail.return_fixed_date || mr.work_detail.parts_replaced));
                this.showWorkDetailsTab = this.fulfillmentDataExists;
            }

            // Load form templates list
            const templatesResp = await lastValueFrom(this.maintenanceRequestService.getFormTemplates()) as any;
            this.templates = templatesResp?.data || templatesResp || [];

            let templateIdToLoad: string | null = this.currentTemplateId || null;
            if (!templateIdToLoad) {
                const defaultTemplate = this.templates.find((t: any) => t.is_default === true) || this.templates[0];
                if (defaultTemplate) {
                    templateIdToLoad = String(defaultTemplate.id);
                }
            }

            let templateData: any = {};
            if (templateIdToLoad) {
                this.currentTemplateId = templateIdToLoad;
                const templateResp = await lastValueFrom(
                    this.maintenanceRequestService.getFormTemplateById(this.currentTemplateId)
                ) as any;
                templateData = templateResp?.data || templateResp || {};
            } else {
                // Fallback: no templates in DB, load from default
                const defaultResp = await lastValueFrom(
                    this.maintenanceRequestService.getDefaultFormTemplate()
                ) as any;
                templateData = defaultResp?.data || defaultResp || {};
            }

            const fields = templateData.fields || [];
            const tabs = templateData.tabs || [];

            if (templateData.template?.id) {
                this.currentTemplateId = String(templateData.template.id);
            }

            if (fields.length > 0) {
                this.allFields = fields;
                this.processFields(this.allFields, tabs);
                this.initializeForm();
                this.setupCustomFormField();
            }

            // Load asset data and prefill
            if (this.assetId) {
                const assetResp = await lastValueFrom(this.assetService.getAsset(this.assetId));
                if (assetResp?.data) {
                    this.assetData = assetResp.data;
                    this.selectedAssetId = String(this.assetData.id || this.assetId);
                    this.prefillForm();
                }
            }

            // Call specialized prefill for View mode records
            if (this.mode === 'view' || this.mode === 'edit') {
                this.prefillViewForm();
                if (this.mode === 'view') {
                    this.form.disable();
                    this.allFields.forEach(f => f.view_only = true);
                }
            } else {
                if (this.form.contains('request_date')) {
                    this.form.patchValue({ request_date: new Date() }, { emitEvent: false });
                }
            }

            // Apply fulfillment field visibility based on current assign_request_to value
            const assignValue = this.form.get('assign_request_to')?.value || 'in_house';
            this.applyFulfillmentFieldVisibility(assignValue);

            // Set part items table visibility based on parts_replaced value
            this.showPartItemsTable = !!this.form.get('parts_replaced')?.value;

            // Lock fields that have data from backend in edit mode
            if (this.mode === 'edit') {
                this.lockFieldsWithData();

                // If fulfillment data exists, make all fulfillment tab fields view-only
                if (this.fulfillmentDataExists) {
                    this.setTabFieldsViewOnly('fulfillment_details');
                }

                // If work detail data exists, make all work detail tab fields view-only
                if (this.workDetailDataExists) {
                    this.setTabFieldsViewOnly('work_details');
                }
            }

        } catch (err) {
            console.error('Error loading maintenance request form data', err);
            this.toasterService.error('Failed to load form data');
        } finally {
            this.isLoading = false;
            this.cdr.detectChanges();
        }
    }

    ngOnDestroy() {
        this.pageActionService.setActions([]);
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ============================================
    // DATA FETCHING
    // ============================================

    async fetchMasters() {
        try {
            const mastersRes: any = await lastValueFrom(
                this.mastersService.getMasters([
                    'departments',
                    'sub-locations',
                    'asset-groups',
                    'asset-sub-groups',
                    'maintenance-types',
                    'asset-maintenance-issue-types',
                    'asset-maintenance-warranty-types'
                ])
            );

            const data = mastersRes?.data || {};

            this.departmentOptions = (data['departments'] || []).map((d: any) => ({
                label: d.name, value: d.id, ...d
            }));

            this.allSubLocations = Array.isArray(data['sub-locations'])
                ? data['sub-locations'] : [];
            this.subLocationOptions = this.allSubLocations.map((sl: any) => ({
                label: sl.name, value: sl.id, ...sl
            }));

            this.assetGroupOptions = (data['asset-groups'] || []).map((g: any) => ({
                label: g.name, value: g.id, ...g
            }));

            this.assetSubGroupOptions = (data['asset-sub-groups'] || []).map((sg: any) => ({
                label: sg.name, value: sg.id, ...sg
            }));

            this.maintenanceTypeOptions = (data['maintenance-types'] || []).map((mt: any) => ({
                label: mt.name, value: mt.id, ...mt
            }));

            this.maintenanceIssueTypeOptions = (data['asset-maintenance-issue-types'] || []).map((it: any) => ({
                label: it.name, value: it.id, ...it
            }));

            this.maintenanceWarrantyTypeOptions = (data['asset-maintenance-warranty-types'] || []).map((wt: any) => ({ 
                label: wt.name, value: wt.id, ...wt
            }));

        } catch (error) {
            console.error('Error fetching masters:', error);
        }
    }

    async loadSubsidiaries() {
        try {
            const res = await lastValueFrom(this.subsidiaryService.getSubsidiaries());
            const data = res?.data?.subsidiaries || res?.data || res || [];
            this.subsidiaries = Array.isArray(data) ? data : [];
            this.subsidiaryOptions = this.subsidiaries.map((s: any) => ({ label: s.name, value: s.id, ...s }));

            // Build location options from all subsidiary tax_information
            const allLocations: any[] = [];
            this.subsidiaries.forEach((s: any) => {
                const taxInfo = s.tax_information || [];
                taxInfo.forEach((t: any) => {
                    allLocations.push({ label: t.address, value: t.id, subsidiary_id: s.id });
                });
            });
            this.locationOptions = allLocations;
        } catch (error) {
            console.error('Error loading subsidiaries:', error);
        }
    }

    // ============================================
    // FIELD OPTIONS
    // ============================================

    assignOptionsToFields(fields: any[]) {
        fields.forEach(f => {
            if (f.field_name === 'form_template_id') {
                f.options = this.normalizeOptions(this.templates);
            }

            // Maintenance Details tab fields
            if (f.field_name === 'request_type') {
                f.options = this.normalizeOptions([
                    { label: 'New Request', value: 'new' },
                    { label: 'Follow Up', value: 'follow_up' }
                ]);
            }

            if (f.field_name === 'issue_type') {
                f.field_type = 'select';
                f.options = this.maintenanceIssueTypeOptions;
            }

            if (f.field_name === 'priority') {
                f.options = this.normalizeOptions([
                    { label: 'Low', value: 'low' },
                    { label: 'Medium', value: 'medium' },
                    { label: 'High', value: 'high' }
                ]);
            }

            // Fulfillment Details tab
            if (f.field_name === 'department') {
                f.field_type = 'select';
                f.options = this.departmentOptions;
            }

            if (f.field_name === 'assign_request_to') {
                f.options = this.normalizeOptions([
                    { label: 'In-House', value: 'in_house' },
                    { label: 'Vendor', value: 'vendor' }
                ]);
            }

            if (f.field_name === 'covered_under') {
                f.options = this.normalizeOptions([
                    { label: 'Warranty', value: 'warranty' },
                    { label: 'AMC', value: 'amc' },
                    { label: 'Insurance', value: 'insurance' },
                    { label: 'None', value: 'none' }
                ]);
            }

            if (f.field_name === 'transport_mode') {
                f.options = this.normalizeOptions([
                    { label: 'Vendor Pickup', value: 'vendor_pickup' },
                    { label: 'Courier', value: 'courier' },
                    { label: 'Self Drop', value: 'self_drop' }
                ]);
            }

            // Work Details tab
            if (f.field_name === 'maintenance_status') {
                f.options = this.normalizeOptions([
                    { label: 'Fixed', value: 'fixed' },
                    { label: 'Temporarily Fixed', value: 'temporarily_fixed' },
                    { label: 'Not Fixed', value: 'not_fixed' },
                    { label: 'Replaced', value: 'replaced' }
                ]);
            }

            if (f.field_name === 'return_mode') {
                f.options = this.normalizeOptions([
                    { label: 'Dropped by Vendor', value: 'dropped_by_vendor' },
                    { label: 'Self Pickup', value: 'self_pickup' },
                    { label: 'Courier', value: 'courier' }
                ]);
            }

            if (f.field_name === 'asset_condition') {
                f.options = this.normalizeOptions([
                    { label: 'Good', value: 'good' },
                    { label: 'Fair', value: 'fair' },
                    { label: 'Poor', value: 'poor' },
                    { label: 'Non-Functional', value: 'non_functional' }
                ]);
            }

            if (f.field_name === 'warranty_on_part') {
                f.field_type = 'select';
                f.options = this.maintenanceWarrantyTypeOptions;
            }

            if (f.options && Array.isArray(f.options) && f.options.length > 0) {
                f.options = this.normalizeOptions(f.options);
            }
        });
    }

    setOptionsForField(fieldName: string, options: any[]) {
        const normalize = this.normalizeOptions(options);
        const field = this.allFields.find(f => f.field_name === fieldName);
        if (field) {
            field.options = normalize;
        }
        this.cdr.markForCheck();
    }

    normalizeOptions(options: any[]): any[] {
        if (!Array.isArray(options)) return [];
        return options.map(opt => {
            if (typeof opt === 'string') return { label: opt, value: opt };
            return {
                label: opt.label || opt.name || opt.display_name || String(opt.value || opt.id),
                value: opt.value !== undefined ? opt.value : opt.id,
                ...opt
            };
        });
    }

    // ============================================
    // FIELD + TAB PROCESSING
    // ============================================

    processFields(fields: any[], tabs: any[] = []) {
        const viewOnlyFileFields = ['warranty_copy', 'amc_copy', 'insurance_copy'];
        // Normalize field_type: backend uses 'file' but DynamicForm expects 'file_upload'
        fields.forEach(f => {
            if (viewOnlyFileFields.includes(f.field_name)) {
                f.view_only = true;
            }
        });

        // In create mode: hide request_id and created_by_name
        if (this.mode === 'create') {
            fields.forEach(f => {
                if (f.field_name === 'request_id' || f.field_name === 'created_by_name') {
                    f.is_visible = false;
                }
            });
        }

        this.assignOptionsToFields(fields);

        // Standalone mode: convert autocomplete fields to plain input (not in view mode)
        if (this.STANDALONE_MODE && this.mode !== 'view') {
            fields.forEach(f => {
                if (this.STANDALONE_CONVERT_TO_INPUT.includes(f.field_name) && f.field_type === 'autocomplete') {
                    f.field_type = 'input';
                }
            });
        }

        // Tab 1 = Primary Information (accordion)
        this.primaryFields = fields
            .filter(f => Number(f.tab_id) === 1)
            .sort((a, b) => a.display_order - b.display_order);

        // In create mode: only show warranty_amc_details and maintenance_details
        // In edit/view mode: show those + fulfillment_details; work_details only if fulfillment data exists
        let allowedTabs: string[];
        if (this.mode === 'create') {
            allowedTabs = ['warranty_amc_details', 'maintenance_details'];
        } else {
            allowedTabs = this.showWorkDetailsTab
                ? ['warranty_amc_details', 'maintenance_details', 'fulfillment_details', 'work_details']
                : ['warranty_amc_details', 'maintenance_details', 'fulfillment_details'];
        }

        const activeTabs = tabs
            .filter(tab => {
                if (tab.is_active === false || tab.system_name === 'primary_information') return false;
                return allowedTabs.includes(tab.system_name);
            })
            .sort((a, b) => a.display_order - b.display_order);

        this.dynamicFormTabs = activeTabs.map((tab, index) => {
            const tabFields = fields
                .filter(f => Number(f.tab_id) === Number(tab.tab_id) && f.is_active !== false)
                .sort((a, b) => a.display_order - b.display_order);

            let activeSubTabs: any[] = [];

            if (tab.sub_tabs && tab.sub_tabs.length > 0) {
                activeSubTabs = tab.sub_tabs
                    .filter((st: any) => st.is_active !== false)
                    .map((subTab: any) => {
                        const subTabFields = tabFields.filter(
                            f => String(f.sub_tab_id) === String(subTab.sub_tab_id) || f.sub_tab_id === undefined || f.sub_tab_id === null
                        );
                        return {
                            system_name: subTab.system_name,
                            name: subTab.display_name || subTab.system_name,
                            key: subTab.key,
                            sub_type: subTab.type,
                            content: subTabFields.filter((f: any) => f.field_type !== 'table'),
                            tableFields: subTabFields.filter((f: any) => f.field_type === 'table'),
                            show_heading: subTab.show_heading ?? false
                        };
                    });
            }

            // Fallback: if no sub_tabs or all sub_tabs have empty content, create a default sub_tab
            const hasContent = activeSubTabs.some((st: any) => st.content && st.content.length > 0);
            if ((!activeSubTabs.length || !hasContent) && tabFields.length > 0) {
                activeSubTabs = [{
                    system_name: tab.system_name,
                    name: tab.display_name || tab.system_name,
                    key: 'default',
                    sub_type: 'FIELD',
                    content: tabFields.filter((f: any) => f.field_type !== 'table'),
                    tableFields: tabFields.filter((f: any) => f.field_type === 'table'),
                    show_heading: false
                }];
            }

            return { system_name: tab.system_name, title: tab.display_name || tab.system_name, value: index, sub_tabs: activeSubTabs };
        }).filter(tab => tab.sub_tabs.length > 0);
    }

    // ============================================
    // FORM INITIALIZATION
    // ============================================

    private initializeForm() {
        this.form = this.fb.group({});

        const addControl = (field: any) => {
            const key = field.field_name;
            if (!this.form.contains(key)) {
                let defaultValue = field.default_value ?? '';
                if (key === 'form_template_id' && this.currentTemplateId) {
                    defaultValue = this.currentTemplateId;
                }
                const control = this.fb.control(defaultValue);
                this.form.addControl(key, control);
            }
        };

        this.primaryFields.forEach(field => addControl(field));

        this.dynamicFormTabs.forEach(tab => {
            tab.sub_tabs.forEach((subTab: any) => {
                if (subTab.sub_type === 'TABLE' || subTab.sub_type === 'table') {
                    // Create a FormArray for TABLE type sub-tabs
                    const tableKey = subTab.key;
                    if (!this.form.contains(tableKey)) {
                        this.form.addControl(tableKey, this.fb.array([]));
                    }
                } else {
                    (subTab.content || []).forEach((field: any) => {
                        // Skip table fields from form controls
                        if (field.field_type === 'table') return;
                        addControl(field);
                    });
                }
            });
        });

        // Ensure form_template_id is set
        if (this.currentTemplateId && this.form.get('form_template_id')) {
            this.form.get('form_template_id')?.patchValue(this.currentTemplateId, { emitEvent: false });
        }
    }

    private setupCustomFormField() {
        const customFormField = this.primaryFields.find(f => f.field_name === 'form_template_id');
        if (!customFormField) return;
        customFormField.options = this.normalizeOptions(this.templates);
        if (this.currentTemplateId) {
            customFormField.default_value = this.currentTemplateId;
            const ctrl = this.form.get('form_template_id');
            if (ctrl && !ctrl.value) {
                ctrl.patchValue(this.currentTemplateId, { emitEvent: false });
            }
        }
    }

    private applyFulfillmentFieldVisibility(assignType: string) {
        const isVendor = assignType === 'vendor';

        // Fulfillment tab fields
        const showFulfillment = isVendor ? this.FULFILLMENT_VENDOR_FIELDS : this.FULFILLMENT_IN_HOUSE_FIELDS;
        const hideFulfillment = isVendor ? this.FULFILLMENT_IN_HOUSE_FIELDS : this.FULFILLMENT_VENDOR_FIELDS;

        // Work Details tab fields
        const showWorkDetail = isVendor ? this.WORK_DETAIL_VENDOR_FIELDS : this.WORK_DETAIL_IN_HOUSE_FIELDS;
        const hideWorkDetail = isVendor ? this.WORK_DETAIL_IN_HOUSE_FIELDS : this.WORK_DETAIL_VENDOR_FIELDS;

        const allShow = [...showFulfillment, ...showWorkDetail];
        const allHide = [...hideFulfillment, ...hideWorkDetail];

        this.allFields.forEach(f => {
            if (allShow.includes(f.field_name)) {
                f.is_visible = true;
            }
            if (allHide.includes(f.field_name)) {
                f.is_visible = false;
                // Reset hidden field values
                const ctrl = this.form.get(f.field_name);
                if (ctrl) {
                    ctrl.patchValue('', { emitEvent: false });
                }
            }
        });

        this.cdr.markForCheck();
    }

    private lockFieldsWithData() {
        // Boolean fields that should lock when work_detail exists (even if false)
        const booleanLockFields = [
            'is_vendor_likely_required',
            'request_to_discard', 'parts_replaced'
        ];
        const hasWorkDetail = !!this.maintenanceRequestData?.work_detail;

        this.LOCK_FIELDS_ON_DATA.forEach(fieldName => {
            const ctrl = this.form.get(fieldName);
            if (!ctrl) return;

            const isBooleanField = booleanLockFields.includes(fieldName);
            const shouldLock = isBooleanField ? hasWorkDetail : !!ctrl.value;

            if (shouldLock) {
                const field = this.allFields.find((f: any) => f.field_name === fieldName);
                if (field) {
                    field.original_field_type = field.original_field_type || field.field_type;
                    if (field.field_type === 'file_upload') {
                        field.view_only = true;
                    } else {
                        field.field_type = 'label';
                    }
                    field.is_mandatory = false;
                }
            }
        });
        this.cdr.markForCheck();
    }

    private setTabFieldsViewOnly(tabSystemName: string) {
        // Find the tab_id for this system_name from dynamicFormTabs
        const tab = this.dynamicFormTabs.find((t: any) => t.system_name === tabSystemName);
        if (!tab) return;

        // Get all field tab_ids that belong to this system_name tab
        // We match by checking fields whose tab_id corresponds to the tab's position in the template
        this.allFields.forEach(f => {
            // Check if field belongs to a sub_tab of this tab
            const belongsToTab = tab.sub_tabs.some((st: any) =>
                (st.content || []).some((c: any) => c.field_name === f.field_name)
            );
            if (belongsToTab) {
                f.original_field_type = f.original_field_type || f.field_type;
                if (f.field_type === 'file_upload') {
                    f.view_only = true;
                } else {
                    f.field_type = 'label';
                }
                f.is_mandatory = false;
            }
        });
        this.cdr.markForCheck();
    }

    getTableFormArray(key: string): FormArray {
        return this.form.get(key) as FormArray || this.fb.array([]);
    }

    onItemFieldChanged(event: any) {
        // Handle item-level field changes if needed
    }

    // ============================================
    // PREFILL FORM WITH ASSET DATA
    // ============================================

    private clearAssetFields() {
        this.assetData = null;
        this.maintenanceScheduleData = [];
        this.formFieldFileNamesMap.clear();

        // All fields from Tab 1, 2 and 3 (labels) should be cleared
        const fieldsToReset = [
            'asset_name', 'asset_code', 'asset_tag', 'serial_no', 'model_no',
            'asset_group', 'asset_sub_group', 'asset_type',
            'source_subsidiary_id', 'source_location_id',
            'assigned_department_id', 'sub_location_id', 'assigned_person',
            'procurement_vendor_name', 'warranty_status', 'amc_started', 'next_maintenance_date',
            'warranty_copy', 'warranty_expiry', 'amc_copy', 'amc_expiry',
            'insurance_policy_no', 'insurance_expiry', 'insurance_copy',
            'periodic_maintenance', 'maintenance_period', 'from_purchase', 
            'continue_after_amc', 'maintenance_type',
            // Maintenance Details
            'request_type', 'issue_type', 'issue_description', 
            'priority', 'issue_photos', 'is_critical'
        ];

        fieldsToReset.forEach(key => {
            const ctrl = this.form.get(key);
            if (ctrl) {
                ctrl.patchValue('', { emitEvent: false });
            }
        });
        this.cdr.detectChanges();
    }

    private isValidId(id: any): boolean {
        if (!id) return false;
        const strId = String(id);
        return strId !== '0' && strId !== '';
    }

    private prefillForm() {
        const asset = this.assetData;
        if (!asset) return;

        // Resolve master names
        const assetGroupName = asset.asset_group_name || this.resolveNameFromOptions(this.assetGroupOptions, asset.asset_group_id);
        const assetSubGroupName = asset.asset_sub_group_name || this.resolveNameFromOptions(this.assetSubGroupOptions, asset.asset_sub_group_id);
        const centerName = asset.subsidiary_name || this.resolveNameFromOptions(this.subsidiaryOptions, asset.subsidiary_id);

        let locationName = asset.location_name || '-';
        if (locationName === '-' && this.isValidId(asset.subsidiary_id)) {
            const subsidiary = this.subsidiaries.find(s => String(s.id) === String(asset.subsidiary_id));
            if (subsidiary && subsidiary.tax_information) {
                const loc = subsidiary.tax_information.find((t: any) => String(t.id) === String(asset.location_id));
                if (loc) locationName = loc.address;
            }
        }

        // Compute warranty status from asset.detail
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let warrantyStatus = '-';
        if (asset.detail?.warranty_expiry_date) {
            const expiry = new Date(asset.detail.warranty_expiry_date);
            warrantyStatus = expiry >= today ? 'Active' : 'Expired';
        }

        // AMC Started logic from asset.detail
        let amcStarted = 'No';
        if (asset.detail?.amc_start_date) {
            if (asset.detail?.amc_expiry_date) {
                const amcExpiry = new Date(asset.detail.amc_expiry_date);
                amcStarted = amcExpiry >= today ? 'Yes' : 'No';
            } else {
                amcStarted = 'Yes';
            }
        }

        // Next Maintenance logic
        let nextMaintenanceDate = '-';
        if (asset.maintenance_items?.length > 0) {
            const upcomingDates = asset.maintenance_items
                .filter((item: any) => item.scheduled_date)
                .map((item: any) => new Date(item.scheduled_date))
                .filter((d: Date) => d >= today)
                .sort((a: Date, b: Date) => a.getTime() - b.getTime());

            if (upcomingDates.length > 0) {
                const nextDate = upcomingDates[0];
                const yy = nextDate.getFullYear();
                const mm = String(nextDate.getMonth() + 1).padStart(2, '0');
                const dd = String(nextDate.getDate()).padStart(2, '0');
                nextMaintenanceDate = `${dd}-${mm}-${yy}`;
            }
        }

        const fieldValueMap: Record<string, any> = {
            asset_name: asset.asset_name || '-',
            asset_code: asset.asset_code || '-',
            asset_tag: asset.barcode || asset.tag_prefix || '-',
            serial_no: asset.serial_number || '-',
            model_no: asset.model_no || '-',
            asset_group: assetGroupName || '-',
            asset_sub_group: assetSubGroupName || '-',
            asset_type: asset.asset_type || '-',
            source_subsidiary_id: centerName || '-',
            source_location_id: locationName || '-',
            assigned_department_id: asset.assigned_to_department_name || '-',
            sub_location_id: asset.sub_location_name || '-',
            assigned_person: asset.assigned_to_user_name || '-',
            procurement_vendor_name: asset.detail?.procurement_vendor_name || '-',
            warranty_status: warrantyStatus,
            amc_started: amcStarted,
            next_maintenance_date: nextMaintenanceDate,
        };

        // Warranty/AMC Details tab fields (Tab 2)
        fieldValueMap['warranty_copy'] = asset.detail?.warranty_copy_url || asset.warranty_copy || '';
        fieldValueMap['warranty_expiry'] = asset.detail?.warranty_expiry_date
            ? this.formatDateDisplay(asset.detail.warranty_expiry_date) : '-';
        fieldValueMap['amc_copy'] = asset.detail?.amc_copy_url || asset.amc_copy || '';
        fieldValueMap['amc_expiry'] = asset.detail?.amc_expiry_date
            ? this.formatDateDisplay(asset.detail.amc_expiry_date) : '-';
        fieldValueMap['insurance_policy_no'] = asset.detail?.insurance_policy_no || asset.insurance_policy_no || '-';
        fieldValueMap['insurance_expiry'] = asset.detail?.insurance_expiry_date
            ? this.formatDateDisplay(asset.detail.insurance_expiry_date) : '-';
        fieldValueMap['insurance_copy'] = asset.detail?.insurance_copy_url || asset.insurance_copy || '';

        // Periodic Maintenance fields
        fieldValueMap['periodic_maintenance'] = asset.detail?.is_periodic_maintenance ? 'Yes' : 'No';
        fieldValueMap['maintenance_period'] = asset.detail?.maintenance_period || asset.maintenance_period || '-';
        fieldValueMap['from_purchase'] = asset.detail?.maint_from_purchase_date ? 'Yes' : 'No';
        fieldValueMap['continue_after_amc'] = asset.detail?.maint_continue_after_amc ? 'Yes' : 'No';

        // Maintenance Type
        if (asset.detail?.maintenance_type_id) {
            const mtName = this.resolveNameFromOptions(this.maintenanceTypeOptions, asset.detail.maintenance_type_id);
            fieldValueMap['maintenance_type'] = mtName || '-';
        } else if (asset.detail?.maintenance_type) {
            fieldValueMap['maintenance_type'] = asset.detail.maintenance_type;
        } else {
            fieldValueMap['maintenance_type'] = '-';
        }

        // Maintenance Schedule table data (from asset.maintenance_items)
        if (asset.maintenance_items && Array.isArray(asset.maintenance_items)) {
            this.maintenanceScheduleData = asset.maintenance_items.map((item: any) => {
                const vendorName = item.vendor_name || item.vendor || '-';
                const sDateRaw = item.scheduled_date || item.schedule_date || item.date;
                const remarksRaw = item.maintenance_remarks || item.remarks || '-';

                return {
                    ...item,
                    vendor: vendorName,
                    schedule_date: sDateRaw ? this.formatDateDisplay(sDateRaw) : '-',
                    remarks: remarksRaw
                };
            });
        }

        // Safe patch like asset-remove
        const safePatch: any = {};
        Object.entries(fieldValueMap).forEach(([key, value]) => {
            if (this.form.contains(key) && value !== undefined && value !== null) {
                safePatch[key] = value;
            }
        });
        this.form.patchValue(safePatch, { emitEvent: false });

        this.cdr.detectChanges();
        this.cdr.detectChanges();
    }

    private prefillViewForm() {
        const mr = this.maintenanceRequestData;
        if (!mr) return;

        const patchData: any = {};

        // Normal payload fields
        const map: Record<string, any> = {
            request_id: mr.maintenance_request_id,
            created_by_name: mr.created_by_name,
            request_type: mr.type_of_request,
            issue_type: mr.issue_type,
            issue_description: mr.description,
            priority: mr.priority,
            issue_photos: mr.photos,
            is_critical: mr.is_critical,
            request_date: mr.request_date,
            
            // Historical asset snapshots stored securely in the MR history
            warranty_status: mr.warranty_status,
            amc_started: mr.amc_started,
            next_maintenance_date: mr.next_maintenance,
            
            // Explicitly re-inject current asset metadata that might otherwise render empty 
            // if form disable() occurs before the async options completely resolve in View Mode
            maintenance_type: this.form.get('maintenance_type')?.value || this.resolveNameFromOptions(this.maintenanceTypeOptions, this.assetData?.detail?.maintenance_type_id) || this.assetData?.detail?.maintenance_type || '-',
        };

        // Show parent maintenance request number if it exists
        if (mr.parent_maintenance_request_no) {
            map['parent_maintenance_request_id'] = mr.parent_maintenance_request_no;
            // Make the field visible
            const parentField = this.allFields.find((f: any) => f.field_name === 'parent_maintenance_request_id');
            if (parentField) {
                parentField.is_visible = true;
            }
        }

        // Flatten custom_fields into patchData so they populate the form controls
        if (mr.custom_fields && typeof mr.custom_fields === 'object') {
            Object.entries(mr.custom_fields).forEach(([key, value]) => {
                if (value !== undefined && value !== null && this.form.contains(key)) {
                    patchData[key] = value;
                }
            });
        }

        // Format date string correctly for the UI if it exists
        if (map['request_date']) {
            patchData['request_date'] = new Date(map['request_date']);
        }

        Object.entries(map).forEach(([key, val]) => {
            if (key !== 'request_date' && this.form.contains(key) && val !== undefined && val !== null) {
                patchData[key] = val;
            }
        });

        // Flatten fulfillment data (Tab 4) into form controls
        if (mr.fulfillment) {
            const f = mr.fulfillment;
            const fulfillmentMap: Record<string, any> = {
                assign_request_to: f.assign_request_to,
                assign_to_user_id: f.assign_to_user_id ? String(f.assign_to_user_id) : null,
                assign_to_user_name: f.assign_to_user_name,
                department: f.department,
                expected_start_date: f.expected_start_date ? new Date(f.expected_start_date) : null,
                expected_resolution_date: f.expected_resolution_date ? new Date(f.expected_resolution_date) : null,
                vendor_name: f.vendor_name,
                contact_person: f.contact_person,
                contact_number: f.contact_number,
                covered_under: f.covered_under,
                estimated_repair_cost: f.estimated_repair_cost,
                need_to_send_out: f.need_to_send_out,
                send_to_location: f.send_to_location,
                dispatch_date: f.dispatch_date ? new Date(f.dispatch_date) : null,
                expected_return_date: f.expected_return_date ? new Date(f.expected_return_date) : null,
                transport_mode: f.transport_mode,
                pickup_challan: f.pickup_challan,
            };
            Object.entries(fulfillmentMap).forEach(([key, val]) => {
                if (this.form.contains(key) && val !== undefined && val !== null) {
                    patchData[key] = val;
                }
            });
            if (f.pickup_challan) {
                this.formFieldFileNamesMap.set('pickup_challan', f.pickup_challan.split('/').pop() || 'challan');
            }
        }

        // Flatten work_detail data (Tab 5) into form controls
        if (mr.work_detail) {
            const w = mr.work_detail;
            const workDetailMap: Record<string, any> = {
                initial_diagnosis: w.initial_diagnosis,
                is_asset_operational: w.is_asset_operational,
                is_vendor_likely_required: w.is_vendor_likely_required,
                request_to_discard: w.request_to_discard,
                return_fixed_date: w.return_fixed_date ? new Date(w.return_fixed_date) : null,
                received_by_user_name: w.received_by_user_name,
                return_mode: w.return_mode,
                delivery_challan: w.delivery_challan,
                maintenance_status: w.maintenance_status,
                vendor_notes: w.vendor_notes,
                pi: w.pi,
                claim_no: w.claim_no,
                amount: w.amount,
                asset_condition: w.asset_condition,
                functional_test_done: w.functional_test_done,
                parts_replaced: w.parts_replaced,
            };
            Object.entries(workDetailMap).forEach(([key, val]) => {
                if (this.form.contains(key) && val !== undefined && val !== null) {
                    patchData[key] = val;
                }
            });
            if (w.delivery_challan) {
                this.formFieldFileNamesMap.set('delivery_challan', w.delivery_challan.split('/').pop() || 'challan');
            }
        }

        this.form.patchValue(patchData, { emitEvent: false });

        // Populate part_items FormArray (Tab 5 TABLE)
        if (mr.part_items && Array.isArray(mr.part_items) && mr.part_items.length > 0) {
            const partItemsArray = this.form.get('part_items') as FormArray;
            if (partItemsArray) {
                partItemsArray.clear();
                mr.part_items.forEach((item: any) => {
                    const rowControls: any = {
                        id: [item.id || ''],
                        part_name: [item.part_name || ''],
                        part_serial_no: [item.part_serial_no || ''],
                        warranty_on_part: [item.warranty_on_part || ''],
                        remarks: [item.remarks || ''],
                    };

                    // Map custom fields to row controls
                    if (item.custom_fields && typeof item.custom_fields === 'object') {
                        Object.entries(item.custom_fields).forEach(([cfKey, cfValue]) => {
                            if (cfValue !== undefined && cfValue !== null) {
                                rowControls[cfKey] = [cfValue];
                            }
                        });
                    }

                    const row = this.fb.group(rowControls);
                    partItemsArray.push(row);
                });
            }
        }

        // Re-display uploaded files seamlessly
        if (mr.photos) {
            this.formFieldFileNamesMap.set('issue_photos', mr.photos.split('/').pop() || 'photo.jpg');
        }

        this.cdr.detectChanges();
    }

    private formatDateDisplay(dateStr: string): string {
        if (!dateStr) return '-';
        try {
            const d = new Date(dateStr);
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yy = d.getFullYear();
            return `${dd}-${mm}-${yy}`;
        } catch {
            return dateStr;
        }
    }

    private resolveNameFromOptions(options: any[], id: any): string {
        if (!id || !options) return '';
        const match = options.find(o => String(o.id) === String(id) || String(o.value) === String(id));
        return match ? (match.label || match.name) : '';
    }

    // ============================================
    // FORM EVENTS
    // ============================================

    onFormFieldChanged(evt: { key: string; value: any; fieldName?: string; [k: string]: any }) {
        if (!evt) return;

        const fieldName = evt.fieldName || evt.key;

        if (fieldName === 'form_template_id') {
            const templateId = typeof evt.value === 'object' ? (evt.value.value || evt.value.id) : evt.value;
            this.handleFormTemplateChange(templateId);
            return;
        }

        if (fieldName === 'parts_replaced') {
            this.showPartItemsTable = !!evt.value;
            this.cdr.markForCheck();
            return;
        }

        // Mutually exclusive: is_vendor_likely_required and request_to_discard
        if (fieldName === 'is_vendor_likely_required' && evt.value === true) {
            const ctrl = this.form.get('request_to_discard');
            if (ctrl && ctrl.value === true) {
                ctrl.patchValue(false, { emitEvent: false });
            }
        }
        if (fieldName === 'request_to_discard' && evt.value === true) {
            const ctrl = this.form.get('is_vendor_likely_required');
            if (ctrl && ctrl.value === true) {
                ctrl.patchValue(false, { emitEvent: false });
            }
        }

        if (fieldName === 'assign_to_user_name') {
            // When user is selected from autocomplete, store the user ID in hidden field
            const selectedOption = typeof evt.value === 'object' ? evt.value : null;
            if (selectedOption?.id && this.form.contains('assign_to_user_id')) {
                this.form.get('assign_to_user_id')?.patchValue(String(selectedOption.id), { emitEvent: false });
            }
        }

        if (fieldName === 'assign_request_to') {
            const assignType = typeof evt.value === 'object' ? (evt.value?.value || evt.value) : evt.value;
            this.applyFulfillmentFieldVisibility(assignType);
            return;
        }

        if (fieldName === 'asset_name' || fieldName === 'asset_id') {
            const rawValue = evt.value;
            const assetId = typeof rawValue === 'object' && rawValue ? (rawValue.value || rawValue.id) : rawValue;
            if (assetId && assetId !== '' && assetId !== null && assetId !== undefined) {
                this.clearAssetFields();
                this.handleAssetSelection(assetId);
            } else {
                this.clearAssetFields();
            }
        }
    }

    // ============================================
    // FILE UPLOAD HANDLERS
    // ============================================

    async onFormFileUpload(event: { file: File; field: any }): Promise<void> {
        const itemIdForUpload = this.selectedAssetId || '0';
        try {
            const result = await this.s3FileUploadService.uploadFileToS3(
                event.file,
                itemIdForUpload,
                'asset'
            );

            const fieldKey = event.field.field_name?.toLowerCase().replace(/[^a-z0-9]/g, '_');
            const control = this.form.get(fieldKey);
            if (control) {
                control.setValue(result.s3Key);
                control.markAsDirty();
                this.formFieldFileNamesMap.set(fieldKey, result.fileName);
                this.cdr.detectChanges();
            }
        } catch (error: any) {
            console.error('File upload failed:', error);
            this.toasterService.error('Upload Failed', 'Failed to upload file');
        }
    }

    async onFileDownloadRequested(event: { s3Key: string; fieldName: string; field: any }): Promise<void> {
        const itemIdForDownload = this.selectedAssetId || '0';
        await this.s3FileUploadService.openPreviewUrl(itemIdForDownload, event.s3Key, 'asset');
    }

    // ============================================
    // ASSET SELECTION
    // ============================================

    private async handleAssetSelection(assetId: any) {
        try {
            const res = await lastValueFrom(this.assetService.getAsset(String(assetId))) as any;
            const asset = res?.data || res || {};

            this.assetData = asset;
            this.selectedAssetId = String(asset.id || assetId);

            this.prefillForm();

            // Show asset name in the autocomplete
            const displayName = asset.asset_name || asset.name || '';
            if (displayName && this.form.contains('asset_name')) {
                this.form.get('asset_name')?.patchValue(displayName, { emitEvent: false });
            }

            this.cdr.detectChanges();
        } catch (err) {
            console.error('Failed to load asset details:', err);
        }
    }

    private async handleFormTemplateChange(templateId: any) {
        if (!templateId || String(templateId) === String(this.currentTemplateId)) return;

        try {
            this.isLoading = true;

            const currentValues = this.form.getRawValue();

            const templateResp = await lastValueFrom(
                this.maintenanceRequestService.getFormTemplateById(String(templateId))
            ) as any;

            if (templateResp?.data?.fields) {
                this.currentTemplateId = String(templateId);
                this.allFields = templateResp.data.fields;
                const tabs = templateResp.data.tabs || [];
                this.processFields(this.allFields, tabs);
                this.initializeForm();
                this.setupCustomFormField();

                this.form.patchValue(currentValues);
                this.form.get('form_template_id')?.patchValue(templateId, { emitEvent: false });

                // Re-prefill asset data after template change
                if (this.assetData) {
                    this.prefillForm();
                }
            }
        } catch (e) {
            console.error('Failed to change form template', e);
            this.toasterService.error('Failed to load template');
        } finally {
            this.isLoading = false;
            this.cdr.detectChanges();
        }
    }

    onSearchQuery(evt: { fieldName: string; query: string }) {
        if (evt.fieldName === 'asset_name' || evt.fieldName === 'asset_id') {
            this.handleAssetSearch(evt.query);
        }
        if (evt.fieldName === 'assign_to_user_name' || evt.fieldName === 'received_by_user_name') {
            this.handleUserSearch(evt.query, evt.fieldName);
        }
        if (evt.fieldName === 'vendor_name') {
            this.handleVendorSearch(evt.query);
        }
        if (evt.fieldName === 'pi') {
            this.handlePiSearch(evt.query);
        }
    }

    async handleAssetSearch(query: string) {
        try {
            const filters: any = { search: query, limit: 20, status: 'active,assigned' };
            const res = await lastValueFrom(this.assetService.getAssets(filters));
            const assets = res?.data?.assets || res?.data || [];
            const options = assets.map((a: any) => {
                const name = a.asset_name || a.name || '';
                const code = a.asset_code || '';
                const label = code ? `${name} (${code})` : name || `AST-${a.id}`;
                return { label, value: a.id, ...a };
            });
            this.setOptionsForField('asset_name', options);
        } catch (err) {
            console.error('Asset search failed', err);
        }
    }

    async handleUserSearch(query: string, fieldName: string = 'assign_to_user_name') {
        try {
            const res = await lastValueFrom(this.userService.getAllUsers({ name: query, limit: 20 }));
            if (res?.users) {
                const options = res.users.map((u: any) => {
                    const name = u.username || u.name || u.email;
                    return { label: name, value: name, id: u.id };
                });
                this.setOptionsForField(fieldName, options);
            }
        } catch (err) {
            console.error('User search failed', err);
        }
    }

    async handleVendorSearch(query: string) {
        try {
            const res = await lastValueFrom(this.vendorService.getAllVendors({ company_name: query, limit: 20 }));
            const vendors = res?.data?.vendors || [];
            const options = vendors.map((v: any) => {
                const name = v.company_name || v.name;
                return { label: name, value: name };
            });
            this.setOptionsForField('vendor_name', options);
        } catch (err) {
            console.error('Vendor search failed', err);
        }
    }

    async handlePiSearch(query: string) {
        try {
            const res = await lastValueFrom(this.invoiceService.getInvoices({ search: query, limit: 20 }));
            const invoices = res?.data?.vendor_invoices || res?.data?.data || [];
            const options = invoices.map((pi: any) => {
                const name = pi.pi_number || pi.invoice_no || pi.vendor_invoice_no || `PI/${pi.id}`;
                return { label: name, value: name };
            });
            this.setOptionsForField('pi', options);
        } catch (err) {
            console.error('PI search failed', err);
        }
    }

    // ============================================
    // HISTORY MODAL
    // ============================================

    openHistoryModal(): void {
        if (this.selectedAssetId || this.assetId) {
            this.historyModalVisible = true;
        }
    }

    // ============================================
    // ACTIONS
    // ============================================

    goBack() {
        if (this.mode === 'edit' || this.mode === 'view') {
            window.history.back();
        } else if (this.assetId) {
            this.router.navigate(['/assets', this.assetId], { queryParams: { mode: 'view' } });
        } else {
            window.history.back();
        }
    }

    async onSubmit() {
        if (this.isSubmitting) return;
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.toasterService.error('Please fill all required fields');
            return;
        }

        this.isSubmitting = true;
        try {
            const payload = this.payloadGenerator.generatePayload<MaintenanceRequestCreatePayload>({
                propertyOrder: [''],
                formGroup: this.form,
                fields: this.allFields,
                dynamicFormTabs: this.dynamicFormTabs,
                fieldToInterfaceMap: MAINTENANCE_REQUEST_FIELD_TO_INTERFACE_MAP,
                skipFields: MAINTENANCE_REQUEST_SKIP_FIELDS,
                getFieldKey: (name) => name,
                tableToInterfaceMap: {},
                transformPayload: (p) => this.transformPayload(p)
            });

            if (this.mode === 'edit' && this.maintenanceRequestId) {
                await lastValueFrom(this.maintenanceRequestService.updateMaintenanceRequest(this.maintenanceRequestId, payload));
                this.toasterService.success('Maintenance request updated successfully');
            } else {
                await lastValueFrom(this.maintenanceRequestService.createMaintenanceRequest(payload));
                this.toasterService.success('Maintenance request created successfully');
            }
            this.goBack();
        } catch (err: any) {
            console.error('Error submitting form', err);

            if (err instanceof PayloadValidationError) {
                this.toasterService.error(err.message);
                this.form.markAllAsTouched();
            } else {
                const errorMsg = err?.error?.message || err?.message || `Failed to ${this.mode === 'edit' ? 'update' : 'create'} maintenance request`;
                this.toasterService.error(errorMsg);
            }
        } finally {
            this.isSubmitting = false;
            this.cdr.detectChanges();
        }
    }

    private transformPayload(payload: any): MaintenanceRequestCreatePayload {
        if (payload.primary) {
            Object.assign(payload, payload.primary);
            delete payload.primary;
        }

        // Rename fields to match backend expectations exactly
        if (payload.request_type !== undefined) {
            payload.type_of_request = payload.request_type;
            delete payload.request_type;
        }
        if (payload.issue_description !== undefined) {
            payload.description = payload.issue_description;
            delete payload.issue_description;
        }
        if (payload.issue_photos !== undefined) {
            payload.photos = payload.issue_photos;
            delete payload.issue_photos;
        }
        
        // Recover next_maintenance_date directly from the form and convert to strict SQL date
        const rawNextMaint = this.form.get('next_maintenance_date')?.value;
        if (!rawNextMaint || rawNextMaint === '-') {
            // Nullify it so the database stays pure for future DATE field migration
            payload.next_maintenance = null; 
        } else {
            // Parse 'DD-MM-YYYY' from the UI prefill into 'YYYY-MM-DD' for the database
            const parts = String(rawNextMaint).split('-');
            if (parts.length === 3) {
                payload.next_maintenance = `${parts[2]}-${parts[1]}-${parts[0]}`;
            } else {
                payload.next_maintenance = rawNextMaint; // Fallback
            }
        }
        
        if (payload.next_maintenance_date !== undefined) {
            delete payload.next_maintenance_date;
        }

        // Set the asset ID
        if (this.selectedAssetId) {
            payload.asset_id = this.selectedAssetId;
        } else if (this.assetId) {
            payload.asset_id = this.assetId;
        }

        if (this.currentTemplateId) {
            payload.form_template_id = this.currentTemplateId;
        }

        // Format request_date
        if (payload.request_date) {
            let dt = payload.request_date;
            if (dt instanceof Date) {
                const yy = dt.getFullYear();
                const mm = String(dt.getMonth() + 1).padStart(2, '0');
                const dd = String(dt.getDate()).padStart(2, '0');
                dt = `${yy}-${mm}-${dd}`;
            }
            payload.request_date = dt;
        }

        // --- Build nested fulfillment object (Tab 4) ---
        const fulfillmentFields = [
            'assign_request_to', 'assign_to_user_id', 'assign_to_user_name', 'department',
            'expected_start_date', 'expected_resolution_date',
            'vendor_name', 'contact_person', 'contact_number', 'covered_under',
            'estimated_repair_cost', 'need_to_send_out', 'send_to_location',
            'dispatch_date', 'expected_return_date', 'transport_mode', 'pickup_challan'
        ];
        const fulfillment: any = {};
        let hasFulfillment = false;
        fulfillmentFields.forEach(key => {
            if (payload[key] !== undefined && payload[key] !== '' && payload[key] !== null) {
                fulfillment[key] = payload[key];
                hasFulfillment = true;
            }
            delete payload[key];
        });
        if (hasFulfillment) {
            payload.fulfillment = fulfillment;
        }

        // --- Build nested work_detail object (Tab 5) ---
        const workDetailFields = [
            'initial_diagnosis', 'is_asset_operational', 'is_vendor_likely_required',
            'request_to_discard', 'return_fixed_date', 'received_by_user_name',
            'return_mode', 'delivery_challan', 'maintenance_status', 'vendor_notes',
            'pi', 'claim_no', 'amount', 'asset_condition', 'functional_test_done',
            'parts_replaced'
        ];
        const workDetail: any = {};
        let hasWorkDetail = false;
        workDetailFields.forEach(key => {
            if (payload[key] !== undefined && payload[key] !== '' && payload[key] !== null) {
                workDetail[key] = payload[key];
                hasWorkDetail = true;
            }
            delete payload[key];
        });
        if (hasWorkDetail) {
            payload.work_detail = workDetail;
        }

        // --- Build part_items array from FormArray ---
        const partItemsArray = this.form.get('part_items') as FormArray;
        if (partItemsArray && partItemsArray.length > 0) {
            const standardKeys = [
                'id', 'part_id', 'part_name', 'part_serial_no',
                'warranty_on_part', 'remarks', 'is_deleted'
            ];

            const partItems = partItemsArray.controls.map((ctrl: any) => {
                const rowValue = ctrl.value || {};
                const mappedRow: any = {};
                const customFields: any = {};

                Object.keys(rowValue).forEach(key => {
                    // is_selected is used by the frontend table checkbox
                    if (key === 'is_selected') return;

                    if (standardKeys.includes(key)) {
                        mappedRow[key] = rowValue[key];
                    } else if (rowValue[key] !== undefined && rowValue[key] !== null) {
                        customFields[key] = rowValue[key];
                    }
                });

                if (Object.keys(customFields).length > 0) {
                    mappedRow['custom_fields'] = customFields;
                }

                return mappedRow;
            });
            payload.part_items = partItems;
        }

        return payload;
    }
}
